import { Component, Input, forwardRef, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    standalone: false,
    selector: 'res-custom-checkbox',
    templateUrl: './custom-checkbox.component.html',
    styleUrls: ['./custom-checkbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomCheckboxComponent),
            multi: true
        }
    ]
})
export class CustomCheckboxComponent implements ControlValueAccessor {
    checkboxId = '_' + window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36);

    @Input() content: string;
    // use contentTemplate if the label content is more complex than simple text
    @Input() contentTemplate: TemplateRef<any>;

    @Input() automationId = 'custom';
    @Input() isToggleBox: boolean;
    @Input() isSkeuomorphic: boolean;
    @Input() isDisabled: boolean;
    @Input() isIndeterminate: boolean;
    @Input() isRequired: boolean;
    @Input() isWBDisabled!: boolean;

    // use marginLeft if the whole checkbox needs a margin left, as it has no margins or paddings by default
    @Input() marginLeft: number;
    // use marginRight if the whole checkbox needs a margin right, as it has no margins or paddings by default
    @Input() marginRight: number;

    // use labelPaddingLeft if the checkbox needs padding left. this will make the checkbox clickable on its whole padding
    @Input() labelPaddingLeft: number;
    // use labelPaddingRight if the checkbox needs padding right. this will make the checkbox clickable on its whole padding
    @Input() labelPaddingRight: number;

    // use iconClass if an icon is needed between the input and the label. if the icon color is part of our colors, use the color class here
    @Input() iconClass: string;

    // use iconHexColor if the color comes from service tier (tags, folders)
    @Input() iconHexColor: string;

    // use notClickable when the input and label shouldn't be clickable, when some other evens already select the checkbox (need so it doesn't trigger selection twice)
    @Input() notClickable: boolean;

    // trigger checkbox click on mousedown instead of click, useful when checkbox doesn't check due to a blur callback (new custodian modal)
    @Input() checkOnMouseDown: boolean;

    // used in toggle box group to hide the left border for easier layout
    @Input() isToggleBoxGroupItem: boolean;

    // add title to checkbox div
    @Input() titleText: string;

    @Input() hasCustomClass?: boolean;

    isChecked: boolean;

    private propagateChange = (_: any) => {
        return;
    };

    writeValue(value: boolean): void {
        this.isChecked = value;
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        return;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onClick(): void {
        if (!this.isDisabled && !this.checkOnMouseDown) {
            this.isChecked = !this.isChecked;
            this.onChange();
        }
    }

    onMouseDown(): void {
        if (!this.isDisabled && this.checkOnMouseDown) {
            this.isChecked = !this.isChecked;
            this.onChange();
        }
    }

    onChange(): void {
        this.propagateChange(this.isChecked);
    }
}
